import { useState, useEffect } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  CheckCircle2,
  Circle,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Sparkles,
  PartyPopper,
  Package,
  FileText,
  ClipboardCheck,
  Link,
  ExternalLink,
  X,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Progress } from '@/components/ui/progress'
import { cn } from '@/lib/utils'
import { useOnboarding } from '@/hooks/useOnboarding'

import confetti from 'canvas-confetti'

// Icon mapping
const iconMap: Record<string, React.ElementType> = {
  Package,
  FileText,
  ClipboardCheck,
  Link,
}

interface OnboardingChecklistProps {
  className?: string
}

export function OnboardingChecklist({ className }: OnboardingChecklistProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const {
    steps,
    completionPercentage,
    isWizardDismissed,
    isChecklistDismissed,
    isOnboardingComplete,
    dismissChecklist,
  } = useOnboarding()

  const [isMinimized, setIsMinimized] = useState(true) // Start minimized
  const [showCelebration, setShowCelebration] = useState(false)
  const [prevCompletion, setPrevCompletion] = useState(completionPercentage)

  // Celebrate completions
  useEffect(() => {
    if (completionPercentage === 100 && prevCompletion < 100) {
      setShowCelebration(true)
      confetti?.({ particleCount: 100, spread: 70, origin: { y: 0.6 } })

      const timer = setTimeout(() => setShowCelebration(false), 3000)
      setPrevCompletion(completionPercentage)
      return () => clearTimeout(timer)
    }
    setPrevCompletion(completionPercentage)
  }, [completionPercentage, prevCompletion])

  // Don't show if wizard not dismissed (wizard is showing instead)
  if (!isWizardDismissed) return null

  // Don't show if complete or user dismissed it
  if (isOnboardingComplete || isChecklistDismissed) return null

  // Minimized view - subtle progress indicator
  if (isMinimized) {
    return (
      <div className={cn('fixed bottom-6 left-4 sm:left-6 z-40 flex items-center gap-1', className)}>
        <Button
          onClick={() => setIsMinimized(false)}
          variant="outline"
          className="bg-white shadow-lg gap-2 hover:shadow-xl transition-shadow"
        >
          <Sparkles className="h-4 w-4 text-certify-600" />
          <span className="font-medium">{completionPercentage}% setup</span>
          <ChevronUp className="h-4 w-4" />
        </Button>
        <Button
          onClick={dismissChecklist}
          variant="outline"
          size="icon"
          className="bg-white shadow-lg hover:shadow-xl transition-shadow h-9 w-9"
          aria-label="Dismiss setup progress"
        >
          <X className="h-4 w-4 text-muted-foreground" />
        </Button>
      </div>
    )
  }

  return (
    <div
      className={cn(
        'fixed bottom-6 left-4 right-4 sm:left-6 sm:right-auto z-40 sm:w-72 bg-white rounded-xl shadow-xl border overflow-hidden',
        className
      )}
    >
      {/* Header */}
      <div className="bg-certify-600 text-white p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <span className="font-medium text-sm">Setup Progress</span>
          </div>
          <button
            onClick={() => setIsMinimized(true)}
            className="p-1 hover:bg-white/20 rounded transition-colors"
            aria-label="Collapse setup progress"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        <div className="flex items-center gap-2">
          <Progress value={completionPercentage} className="h-1.5 flex-1 bg-white/20 [&>div]:bg-white" />
          <span className="text-xs font-medium">{completionPercentage}%</span>
        </div>

        {showCelebration && (
          <div className="mt-2 bg-white/20 rounded p-2 flex items-center gap-2 text-sm">
            <PartyPopper className="h-4 w-4" />
            <span>All done! 🎉</span>
          </div>
        )}
      </div>

      {/* Steps */}
      <div className="max-h-48 overflow-y-auto">
        {steps.map((step) => {
          const Icon = iconMap[step.icon] || Circle
          const isActive = location.pathname === step.path

          return (
            <button
              key={step.id}
              onClick={() => navigate(step.path)}
              className={cn(
                'w-full flex items-center gap-2 p-3 text-left hover:bg-gray-50 transition-colors border-b last:border-b-0',
                isActive && 'bg-certify-50'
              )}
            >
              <div
                className={cn(
                  'w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0',
                  step.completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-400'
                )}
              >
                {step.completed ? <CheckCircle2 className="h-4 w-4" /> : <Icon className="h-3 w-3" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className={cn('text-sm font-medium truncate', step.completed && 'text-muted-foreground')}>
                  {step.title}
                </p>
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
            </button>
          )
        })}
      </div>

      {/* Dismiss */}
      <div className="p-2 bg-gray-50 border-t">
        <Button
          variant="ghost"
          size="sm"
          className="w-full text-xs text-muted-foreground"
          onClick={dismissChecklist}
        >
          Dismiss setup guide
        </Button>
      </div>
    </div>
  )
}

/**
 * Inline version for embedding in pages
 */
export function InlineOnboardingChecklist({ className }: { className?: string }) {
  const navigate = useNavigate()
  const { steps, completionPercentage, isOnboardingComplete } = useOnboarding()

  if (isOnboardingComplete || steps.length === 0) return null

  const incompleteSteps = steps.filter((s) => !s.completed)
  if (incompleteSteps.length === 0) return null

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex items-center justify-between">
        <h4 className="text-sm font-medium">Setup Progress</h4>
        <span className="text-sm text-muted-foreground">{completionPercentage}%</span>
      </div>
      <Progress value={completionPercentage} className="h-2" />
      <div className="space-y-1">
        {incompleteSteps.slice(0, 2).map((step) => {
          const Icon = iconMap[step.icon] || Circle
          return (
            <button
              key={step.id}
              onClick={() => navigate(step.path)}
              className="w-full flex items-center gap-2 p-2 text-left hover:bg-muted rounded-lg transition-colors"
            >
              <div className="w-6 h-6 rounded-full bg-gray-100 flex items-center justify-center">
                <Icon className="h-3 w-3 text-gray-400" />
              </div>
              <span className="text-sm flex-1">{step.title}</span>
              <ExternalLink className="h-3 w-3 text-muted-foreground" />
            </button>
          )
        })}
      </div>
    </div>
  )
}
